import React, { useState, useEffect, useCallback } from 'react';
import type { SituationRegistry, SituationMode } from '../../types/agent.types';
import { agentAPI } from '../../api/agent.api';
import './ControlView.css';

// Human-readable descriptions for each situation
const DESCRIPTIONS: Record<string, { what: string; trigger: string; action: string; off: string }> = {
  rappel_paiement_j1: {
    what: 'Rappel paiement J+1',
    trigger: 'Visite effectuee, facture envoyee, pas de paiement apres 1 jour',
    action: 'Email de rappel au client avec references de paiement',
    off: 'Le bureau envoie manuellement les rappels J+1',
  },
  rappel_paiement_j8: {
    what: 'Rappel paiement J+8',
    trigger: '7 jours apres le 1er rappel, toujours impaye',
    action: 'Email + SMS au client, ton plus insistant',
    off: 'Le bureau envoie les rappels J+8',
  },
  rappel_paiement_j15: {
    what: 'Rappel paiement J+15',
    trigger: '7 jours apres le 2eme rappel, toujours impaye',
    action: 'Email ferme avec avertissement de mise en demeure',
    off: 'Le bureau gere les rappels J+15',
  },
  rappel_paiement_post_rapport: {
    what: 'Rappels post-rapport',
    trigger: 'Rapport transmis, facture envoyee, pas de paiement apres 15/30/45 jours',
    action: 'Suivi de paiement echelonne apres livraison du rapport',
    off: 'Suivi post-rapport manuel',
  },
  escalade_impaye: {
    what: 'Escalade Solvency',
    trigger: '3 rappels envoyes, toujours impaye',
    action: 'Propose lettre de recouvrement via Solvency',
    off: 'Frederic identifie les dossiers a escalader',
  },
  paiement_inactif_30j: {
    what: 'Dossier inactif >30j',
    trigger: 'Aucune activite sur un dossier impaye depuis 30+ jours',
    action: 'Alerte avec proposition de relance ou mise sans suite',
    off: 'Les dossiers inactifs peuvent passer inapercus',
  },
  attente_confirmation_relance: {
    what: 'Relance confirmation courtier',
    trigger: 'Pre-rapport envoye, pas de reponse courtier/client depuis 3+ jours',
    action: 'Email de relance au courtier pour obtenir confirmation',
    off: 'Le bureau suit les confirmations manuellement',
  },
  attente_doc_relance: {
    what: 'Relance documents manquants',
    trigger: 'Dossier en attente doc/infos depuis 3+ jours, pas de reponse',
    action: 'Email de relance au client/courtier pour les documents',
    off: 'Le bureau relance manuellement pour les docs',
  },
  courtier_conteste_valeur: {
    what: 'Contestation courtier',
    trigger: 'Courtier conteste la valeur ou le contenu du rapport',
    action: 'Analyse la contestation et propose reponse (route vers FCH/evaluateur)',
    off: 'Frederic detecte les contestations lui-meme',
  },
  valorisation_validation: {
    what: 'Validation valorisation',
    trigger: 'Dossier paye, rapport expert recu, pret pour valorisation',
    action: 'Detecte et signale les dossiers prets pour Frederic',
    off: 'Frederic verifie chaque dossier manuellement',
  },
  email_notification: {
    what: 'Notification email',
    trigger: 'Nouvel email non traite sur un dossier',
    action: 'Cree un tracker_com pour alerter le bureau',
    off: 'Les emails peuvent rester sans alerte',
  },
  email_analysis: {
    what: 'Analyse email complete',
    trigger: 'Email non lu recu sur un dossier actif',
    action: 'Lit le contenu, identifie le type (paiement, docs, question...) et propose l\'action adaptee',
    off: 'Le bureau lit et route chaque email manuellement',
  },
  expert_en_retard: {
    what: 'Relance expert en retard',
    trigger: 'Rapport non remis 48h apres la visite, pas de signal d\'attente docs',
    action: 'Email de relance a l\'expert avec rappel des delais',
    off: 'Le bureau surveille les deadlines experts',
  },
  email_archived_dossier: {
    what: 'Email sur dossier archive',
    trigger: 'Email recu sur un dossier archive/sans suite/annule',
    action: 'Analyse si reouverture necessaire, route vers FCH si oui',
    off: 'Le bureau gere les emails sur archives',
  },
  email_assign_bureau: {
    what: 'Assignation email au bureau',
    trigger: 'Email recu necessitant une action bureau',
    action: 'Choisit le bon membre selon les regles d\'assignation et cree un tracker_com',
    off: 'Le bureau s\'organise pour les emails',
  },
  email_fch_routing: {
    what: 'Routage FCH',
    trigger: 'Email detecte comme necessitant l\'intervention de Frederic',
    action: 'Alerte Frederic avec contexte et proposition d\'action',
    off: 'Frederic trie ses propres emails',
  },
  visit_sms: {
    what: 'SMS rappel visite J-1',
    trigger: 'Visite programmee demain, client a un numero mobile',
    action: 'SMS automatique de rappel avec date, heure et adresse',
    off: 'Pas de rappel SMS, risque de no-show',
  },
  daily_briefing: {
    what: 'Briefing quotidien',
    trigger: 'Chaque matin a 7h30',
    action: 'Resume du pipeline: visites du jour, dossiers en retard, paiements en attente',
    off: 'Pas de briefing automatique',
  },
  controle_qualite_rapport: {
    what: 'Controle qualite rapport',
    trigger: 'Rapport expert recu, avant transmission au courtier',
    action: 'Verifie la coherence du rapport (surfaces, photos, PEB)',
    off: 'Frederic verifie la qualite manuellement',
  },
  email_contestation: {
    what: 'Detection contestation',
    trigger: 'Email contenant des mots-cles de contestation (valeur, erreur, reclamation)',
    action: 'Route vers FCH avec analyse du type de contestation',
    off: 'Les contestations sont detectees manuellement',
  },
  email_rapport: {
    what: 'Detection rapport entrant',
    trigger: 'Expert envoie son rapport par email',
    action: 'Detecte et classe le rapport, avance le pipeline',
    off: 'Le bureau detecte les rapports manuellement',
  },
  email_valorisation: {
    what: 'Email impact valorisation',
    trigger: 'Email contenant des docs impactant la valorisation (baux, compromis, acte)',
    action: 'Route vers FCH pour analyse d\'impact',
    off: 'Le bureau detecte les impacts valo manuellement',
  },
  tarification_speciale: {
    what: 'Tarification speciale',
    trigger: 'Dossier necessitant un tarif hors grille (commerce, industrie, special)',
    action: 'Alerte FCH pour decision de prix',
    off: 'Frederic detecte les cas speciaux lui-meme',
  },
};

const MODE_STYLES: Record<SituationMode, { label: string; color: string; bg: string }> = {
  autonomous: { label: 'Automatique', color: '#16a34a', bg: '#dcfce7' },
  propose:    { label: 'Actif',       color: '#2563eb', bg: '#dbeafe' },
  shadow:     { label: 'Observation', color: '#ca8a04', bg: '#fef9c3' },
  disabled:   { label: 'Desactive',   color: '#6b7280', bg: '#f3f4f6' },
};

export const ControlView: React.FC = () => {
  const [situations, setSituations] = useState<SituationRegistry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const fetchRegistry = useCallback(async () => {
    try {
      const response = await agentAPI.getRegistry();
      setSituations(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegistry();
    const interval = setInterval(fetchRegistry, 30000);
    return () => clearInterval(interval);
  }, [fetchRegistry]);

  const setMode = async (id: string, newMode: SituationMode, reason: string) => {
    setUpdating(id);
    try {
      await agentAPI.updateSituation(id, { mode: newMode, mode_change_reason: reason } as any);
      await fetchRegistry();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const killAll = async () => {
    if (!window.confirm('TOUT DESACTIVER ?\n\nLea arretera immediatement toutes les actions.\nVous devrez reactiver manuellement.')) return;
    try {
      await agentAPI.killAll();
      await fetchRegistry();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <div className="ctrl-loading">Chargement...</div>;

  const auto = situations.filter(s => s.mode === 'autonomous');
  const active = situations.filter(s => s.mode === 'propose');
  const off = situations.filter(s => s.mode === 'disabled' || s.mode === 'shadow');

  return (
    <div className="ctrl-v2">
      {error && <div className="ctrl-error">{error} <button onClick={() => setError(null)}>x</button></div>}

      {/* Automatique */}
      {auto.length > 0 && (
        <Section
          title="Automatique"
          count={auto.length}
          desc="Lea execute directement, sans validation."
        >
          {auto.map(s => (
            <Card key={s.id} s={s} updating={updating === s.id}
              actions={[
                { label: 'Passer en actif (avec validation)', style: 'secondary', onClick: () => setMode(s.id, 'propose', 'Passe en actif par FCH') },
                { label: 'Desactiver', style: 'danger', confirm: `Desactiver "${getDesc(s.id)}"?\n\n${DESCRIPTIONS[s.id]?.off || ''}`, onClick: () => setMode(s.id, 'disabled', 'Desactive par FCH') },
              ]}
            />
          ))}
        </Section>
      )}

      {/* Actif */}
      <Section
        title="Actif"
        count={active.length}
        desc="Lea cree des plans. Vous validez dans l'onglet Plans avant execution."
        extra={<button className="ctrl-kill-btn" onClick={killAll}>Tout desactiver</button>}
      >
        {active.length === 0 && <div className="ctrl-empty">Aucune situation active</div>}
        {active.map(s => (
          <Card key={s.id} s={s} updating={updating === s.id}
            actions={[
              { label: 'Passer en automatique', style: 'secondary', confirm: `Passer "${getDesc(s.id)}" en automatique ?\n\nLea executera SANS demander validation.`, onClick: () => setMode(s.id, 'autonomous', 'Passe en auto par FCH') },
              { label: 'Desactiver', style: 'danger', confirm: `Desactiver "${getDesc(s.id)}"?\n\n${DESCRIPTIONS[s.id]?.off || ''}`, onClick: () => setMode(s.id, 'disabled', 'Desactive par FCH') },
            ]}
          />
        ))}
      </Section>

      {/* Desactive */}
      {off.length > 0 && (
        <Section
          title="Desactive"
          count={off.length}
          desc="Lea ignore ces situations."
        >
          {off.map(s => (
            <Card key={s.id} s={s} updating={updating === s.id}
              actions={[
                { label: 'Activer (avec validation)', style: 'primary', confirm: `Activer "${getDesc(s.id)}" ?\n\nLea commencera a creer des plans pour cette situation.`, onClick: () => setMode(s.id, 'propose', 'Active par FCH') },
              ]}
            />
          ))}
        </Section>
      )}
    </div>
  );
};

function getDesc(id: string) { return DESCRIPTIONS[id]?.what || id; }

// ============================================================================
// Section
// ============================================================================
const Section: React.FC<{ title: string; count: number; desc: string; extra?: React.ReactNode; children: React.ReactNode }> = ({ title, count, desc, extra, children }) => (
  <div className="ctrl-section">
    <div className="ctrl-section-head">
      <div>
        <h3 className="ctrl-section-title">{title} ({count})</h3>
        <p className="ctrl-section-desc">{desc}</p>
      </div>
      {extra}
    </div>
    {children}
  </div>
);

// ============================================================================
// Card
// ============================================================================
interface CardAction {
  label: string;
  style: 'primary' | 'secondary' | 'danger';
  confirm?: string;
  onClick: () => void;
}

const Card: React.FC<{ s: SituationRegistry; updating: boolean; actions: CardAction[] }> = ({ s, updating, actions }) => {
  const desc = DESCRIPTIONS[s.id];
  const style = MODE_STYLES[s.mode];
  const hasStats = s.plans_7d > 0 || s.shadow_count_7d > 0;
  const rate = s.approval_rate_7d;
  const rateColor = rate === null ? '#9ca3af' : rate >= 85 ? '#16a34a' : rate >= 70 ? '#ca8a04' : '#dc2626';

  return (
    <div className={`ctrl-card ${updating ? 'ctrl-card--updating' : ''}`}>
      <div className="ctrl-card-top">
        <div className="ctrl-card-info">
          <div className="ctrl-card-name">{s.display_name}</div>
        </div>
        <div className="ctrl-card-right">
          {hasStats && (
            <div className="ctrl-card-stats">
              {s.plans_7d > 0 && <span><strong>{s.plans_7d}</strong> plans/7j</span>}
              {rate !== null && <span style={{ color: rateColor }}><strong>{rate}%</strong> ok</span>}
              {s.shadow_count_7d > 0 && <span><strong>{s.shadow_count_7d}</strong> obs.</span>}
            </div>
          )}
          <span className="ctrl-card-badge" style={{ color: style.color, backgroundColor: style.bg }}>
            {style.label}
          </span>
        </div>
      </div>
      {desc && (
        <div className="ctrl-card-detail">
          <div className="ctrl-card-detail-row">
            <span className="ctrl-card-detail-label">Declencheur</span>
            <span className="ctrl-card-detail-value">{desc.trigger}</span>
          </div>
          <div className="ctrl-card-detail-row">
            <span className="ctrl-card-detail-label">Action</span>
            <span className="ctrl-card-detail-value">{desc.action}</span>
          </div>
          <div className="ctrl-card-detail-row">
            <span className="ctrl-card-detail-label">Parametres</span>
            <span className="ctrl-card-detail-value">
              Confiance min. {Math.round((s.confidence_threshold || 0.8) * 100)}%
              {' · '}Cooldown {s.cooldown_days_completed || '?'}j
              {' · '}Max {s.max_plans_per_7_days || '?'} plans/7j
            </span>
          </div>
        </div>
      )}
      <div className="ctrl-card-actions">
        {actions.map((a, i) => (
          <button
            key={i}
            className={`ctrl-btn ctrl-btn--${a.style}`}
            disabled={updating}
            onClick={() => {
              if (a.confirm && !window.confirm(a.confirm)) return;
              a.onClick();
            }}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ControlView;
